/*
 * THEME CHANGES, ONCE, FOR EVERY TILE.
 *
 * A widget is handed resolved colour VALUES at startup - it never sees the
 * app's stylesheet, because it renders in a frame with an opaque origin and a
 * CSS custom property does not cross that boundary. That is what makes the
 * palette correct at mount and frozen afterwards: flip the app to dark and every
 * tile keeps painting the light one until something remounts it.
 *
 * The host already knows how to re-send the palette. What was missing is anyone
 * telling it to, and the honest place for that is the host itself - a tile that
 * has to be told about the theme by its page is a tile that only works on pages
 * that remember.
 *
 * ONE observer for all of them rather than one each: a hundred-tile board would
 * otherwise attach a hundred MutationObservers to the same attribute on the same
 * element, all firing on the same change.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let observer: MutationObserver | undefined;

/**
 * Call `fn` whenever the app's theme changes. Returns an unsubscribe; the
 * observer stops when the last listener leaves, so a page with no tiles costs
 * nothing.
 */
export function watchTheme(fn: Listener): () => void {
  listeners.add(fn);
  if (!observer) {
    observer = new MutationObserver(() => {
      for (const l of listeners) l();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) {
      observer?.disconnect();
      observer = undefined;
    }
  };
}
